import {
  ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES,
  ADMIN_ARTS_GENOME_TYPE_PERMISSION_MODULES,
  ADMIN_NAV_PERMISSION_MODULES,
  ADMIN_REQUEST_TAB_PERMISSION_MODULES,
} from "../config/adminPermissionRegistry"
import { resolveAdminRoutePermission } from "../config/adminRoutePermissions"
import { isCityAdminUser } from "./cityPortalRoles"

export const ADMIN_PERMISSION_ACTIONS = {
  VIEW: "view",
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  FULL: "full",
}

const EMPTY_GRANT = {
  view: false,
  create: false,
  edit: false,
  delete: false,
  full: false,
}

/**
 * Parse permission.name → frontend action.
 * Examples: cities.read → view, cities.all → full, photography → full
 */
export const parsePermissionAction = name => {
  const raw = String(name || "")
  const parts = raw.split(".")
  const last = parts[parts.length - 1]?.toLowerCase() || ""

  if (last === "all") return ADMIN_PERMISSION_ACTIONS.FULL
  if (last === "read") return ADMIN_PERMISSION_ACTIONS.VIEW
  if (last === "create") return ADMIN_PERMISSION_ACTIONS.CREATE
  if (last === "update") return ADMIN_PERMISSION_ACTIONS.EDIT
  if (last === "delete") return ADMIN_PERMISSION_ACTIONS.DELETE

  if (parts.length === 1 && raw) return ADMIN_PERMISSION_ACTIONS.FULL

  return null
}

const applyGrantAction = (grant, action) => {
  if (!action) return grant

  if (action === ADMIN_PERMISSION_ACTIONS.FULL) {
    return {
      view: true,
      create: true,
      edit: true,
      delete: true,
      full: true,
    }
  }

  return {
    ...grant,
    [action]: true,
  }
}

const mergeGrant = (current = EMPTY_GRANT, incoming = EMPTY_GRANT) => ({
  view: current.view || incoming.view,
  create: current.create || incoming.create,
  edit: current.edit || incoming.edit,
  delete: current.delete || incoming.delete,
  full: current.full || incoming.full,
})

const pushPermissionList = (target, list) => {
  if (!Array.isArray(list)) return
  list.forEach(permission => {
    if (permission) target.push(permission)
  })
}

/**
 * Collect permissions from /me: roles, currentRoles, or a top-level list.
 */
const collectUserPermissions = user => {
  const permissions = []
  pushPermissionList(permissions, user?.permissions)
  ;(user?.roles || []).forEach(role =>
    pushPermissionList(permissions, role?.permissions),
  )
  ;(user?.currentRoles || []).forEach(role =>
    pushPermissionList(permissions, role?.permissions),
  )
  return permissions
}

/**
 * Flatten roles[].permissions[] from GET /me into module grants.
 */
export const normalizeAdminPermissions = (user = null) => {
  const isSuperAdmin = user?.type === "super_admin"

  if (isSuperAdmin) {
    return {
      isSuperAdmin: true,
      isAdminStaff: false,
      grants: {},
      isReady: Boolean(user),
    }
  }

  const isAdminStaff = user?.type === "admin" || isCityAdminUser(user)
  const grants = {}

  if (!isAdminStaff) {
    return {
      isSuperAdmin: false,
      isAdminStaff: false,
      grants,
      isReady: Boolean(user),
    }
  }

  collectUserPermissions(user).forEach(permission => {
    const module =
      permission?.type || String(permission?.name || "").split(".")[0]
    if (!module) return

    const action = parsePermissionAction(permission?.name)
    const current = grants[module] || { ...EMPTY_GRANT }
    const next = applyGrantAction(current, action)
    grants[module] = mergeGrant(current, next)
  })

  return {
    isSuperAdmin: false,
    isAdminStaff: true,
    grants,
    isReady: Boolean(user),
  }
}

/**
 * Check if user can perform action on a permission module.
 * VIEW is never implied by create / edit / delete — only by view or full.
 */
export const canAdminPermission = (
  permissionState,
  module,
  action = ADMIN_PERMISSION_ACTIONS.VIEW,
) => {
  if (!permissionState) return false
  if (permissionState.isSuperAdmin) return true
  if (!permissionState.isAdminStaff) return false
  if (!module) return true

  const grant = permissionState.grants?.[module]
  if (!grant) return false
  if (grant.full) return true
  if (action === ADMIN_PERMISSION_ACTIONS.FULL) return Boolean(grant.full)
  if (action === ADMIN_PERMISSION_ACTIONS.VIEW) {
    return Boolean(grant.view)
  }

  return Boolean(grant[action])
}

/**
 * True when user has explicit view (or full) on a module.
 * Create / edit / delete alone do NOT unlock listing or sidebar.
 */
export const canAdminViewModule = (permissionState, module) => {
  if (!permissionState) return false
  if (permissionState.isSuperAdmin) return true
  if (!permissionState.isAdminStaff) return false
  if (!module) return true

  const grant = permissionState.grants?.[module]
  if (!grant) return false
  return Boolean(grant.full || grant.view)
}

/**
 * True when user has view on any module in the list.
 */
export const canAdminAnyModule = (permissionState, modules = []) => {
  if (!Array.isArray(modules) || modules.length === 0) return false
  if (permissionState?.isSuperAdmin) return true

  return modules.some(module => canAdminViewModule(permissionState, module))
}

/**
 * True when user has view on every module in the list.
 */
export const canAdminAllViewModules = (permissionState, modules = []) => {
  if (!Array.isArray(modules) || modules.length === 0) return false
  if (permissionState?.isSuperAdmin) return true

  return modules.every(module => canAdminViewModule(permissionState, module))
}

/**
 * Whether a sidebar nav item should be visible.
 * Requires VIEW on at least one mapped module — edit/create/delete alone hide the item.
 */
export const canSeeAdminNavItem = (permissionState, navKey) => {
  if (!permissionState) return false
  if (permissionState.isSuperAdmin) return true

  const modules = ADMIN_NAV_PERMISSION_MODULES[navKey]

  // Explicitly public nav (dashboard / settings)
  if (modules === null) return true

  // Unknown key — fail closed so unmapped items never leak into the sidebar
  if (!Array.isArray(modules)) return false

  return canAdminAnyModule(permissionState, modules)
}

/**
 * Whether the current pathname is allowed for admin staff.
 */
export const canAccessAdminRoute = (permissionState, pathname) => {
  if (!permissionState) return false
  if (permissionState.isSuperAdmin) return true

  const path =
    String(pathname || "")
      .split("?")[0]
      .replace(/\/+$/, "") || "/admin"

  const artsGenomeMatch = path.match(
    /^\/admin\/arts-genome(?:\/([^/]+)(?:\/([^/]+)(?:\/([^/]+))?)?)?$/,
  )
  if (artsGenomeMatch) {
    const [, type, segment, trailing] = artsGenomeMatch
    if (!type) {
      return canAdminAnyModule(permissionState, [
        "spellbooks",
        "role-types",
        "role-classes",
      ])
    }

    const module =
      ADMIN_ARTS_GENOME_TYPE_PERMISSION_MODULES[type] || "spellbooks"

    if (segment === "create") {
      return canAdminPermission(
        permissionState,
        module,
        ADMIN_PERMISSION_ACTIONS.CREATE,
      )
    }
    if (trailing === "edit") {
      return canAdminPermission(
        permissionState,
        module,
        ADMIN_PERMISSION_ACTIONS.EDIT,
      )
    }
    return canAdminPermission(
      permissionState,
      module,
      ADMIN_PERMISSION_ACTIONS.VIEW,
    )
  }

  const rule = resolveAdminRoutePermission(pathname)
  if (!rule) return false

  let allowed = false

  if (rule.anyOf?.length) {
    if (rule.action === ADMIN_PERMISSION_ACTIONS.VIEW) {
      allowed = canAdminAnyModule(permissionState, rule.anyOf)
    } else {
      allowed = rule.anyOf.some(module =>
        canAdminPermission(permissionState, module, rule.action),
      )
    }
  } else if (!rule.module) {
    allowed = true
  } else if (rule.action === ADMIN_PERMISSION_ACTIONS.VIEW) {
    allowed = canAdminViewModule(permissionState, rule.module)
  } else {
    allowed = canAdminPermission(permissionState, rule.module, rule.action)
  }

  if (!allowed) return false
  if (!Array.isArray(rule.allOf) || rule.allOf.length === 0) return true
  return canAdminAllViewModules(permissionState, rule.allOf)
}

/**
 * First allowed admin nav path for redirect fallback.
 */
export const getDefaultAdminPath = permissionState => {
  if (canSeeAdminNavItem(permissionState, "dashboard")) {
    return "/admin/dashboard"
  }

  const orderedKeys = Object.keys(ADMIN_NAV_PERMISSION_MODULES)
  const firstAllowed = orderedKeys.find(key =>
    canSeeAdminNavItem(permissionState, key),
  )
  if (!firstAllowed) return "/admin/dashboard"

  const navPaths = {
    dashboard: "/admin/dashboard",
    cities: "/admin/cities",
    projects: "/admin/projects",
    sessions: "/admin/sessions",
    members: "/admin/members",
    rewards: "/admin/rewards",
    inbox: "/admin/inbox",
    "arts-genome": "/admin/arts-genome",
    requests: "/admin/requests",
    flags: "/admin/flags",
    staff: "/admin/staff",
    roles: "/admin/roles",
    settings: "/admin/settings",
  }

  return navPaths[firstAllowed] || "/admin/dashboard"
}

/**
 * Filter Admin Requests tabs by staff permissions.
 */
export const filterAdminRequestTabs = (permissionState, tabs = []) => {
  if (permissionState?.isSuperAdmin) return tabs

  return tabs.filter(tab => {
    const module = ADMIN_REQUEST_TAB_PERMISSION_MODULES[tab.key]
    if (!module) return true
    return canAdminPermission(
      permissionState,
      module,
      ADMIN_PERMISSION_ACTIONS.VIEW,
    )
  })
}

/**
 * Filter Arts Genome tabs by staff permissions.
 */
export const filterAdminArtsGenomeTabs = (permissionState, tabs = []) => {
  if (permissionState?.isSuperAdmin) return tabs

  return tabs.filter(tab => {
    const module = ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES[tab.key]
    if (!module) return true
    return canAdminPermission(
      permissionState,
      module,
      ADMIN_PERMISSION_ACTIONS.VIEW,
    )
  })
}
